// WAAP website-level security actions.
//   GET/PUT /security     → reversible protection toggle (flip the vendor
//                           `waap_enabled` flag; non-destructive — keeps the
//                           site config so it can be turned back on).
//   POST /reset-security  → legacy "Remove Security Settings" (disables WAAP AND
//                           deletes the site config). Kept for back-compat.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapPath, WAAP_AREAS } from './_shared';

/** Current WAAP protection state for one website (`GET /security`). */
export function useWaapProtection(domainId: string | undefined) {
  return useQuery({
    queryKey: ['waap', 'domain', domainId ?? '', 'security'],
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<{ enabled: boolean }> => {
      const res = await serviceApiV3.get<{ enabled?: boolean }>(
        waapPath(domainId as string, WAAP_AREAS.security),
      );
      return { enabled: !!res.data?.enabled };
    },
  });
}

/** Enable/disable WAAP protection for one website (`PUT /security`). Reversible:
 *  the vendor keeps the site config, so flipping back on restores protection. */
export function useWaapSetProtection(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { enabled: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ enabled }: { enabled: boolean }) => {
      const res = await serviceApiV3.put(waapPath(domainId, WAAP_AREAS.security), { enabled });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['waap', 'domain', domainId, 'security'] });
      qc.invalidateQueries({ queryKey: ['waap', 'cdn-domains'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Remove a website's WAAP security settings (legacy destructive reset). Pass the
 *  domain id at call time so one hook instance can serve every row in the list. */
export function useWaapResetSecurity(
  options?: UseMutationOptions<unknown, unknown, { domainId: string }>,
) {
  return useMutation({
    mutationFn: async ({ domainId }: { domainId: string }) => {
      const res = await serviceApiV3.post(waapPath(domainId, WAAP_AREAS.resetSecurity));
      return res.data;
    },
    ...options,
  });
}
